import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { formatDateTime } from "@/shared/lib/date-time";
import { Badge, Button, Checkbox, Modal } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, Edit, ExternalLink, HelpCircle } from "lucide-react";
import { useState } from "react";
import { checklistsApi } from "../api/client";
import {
  useChecklistKpiMappings,
  useChecklistMappings,
  useChecklistQuery,
  useCreateChecklistMapping,
  useUpdateChecklistMapping,
} from "../api/queries";
import { ChecklistErrorState } from "../components/ChecklistErrorState";
import { ChecklistLoadingState } from "../components/ChecklistLoadingState";
import { ChecklistNotFoundState } from "../components/ChecklistNotFoundState";
import {
  ChecklistDetailTour,
  useChecklistDetailTour,
} from "../tour/ChecklistDetailTour";
import { ChecklistKpiMappingAddModal } from "../ui/ChecklistKpiMappingAddModal";
import { ChecklistKpiMappingsTable } from "../ui/ChecklistKpiMappingsTable";
import { ChecklistMappingForm } from "../ui/ChecklistMappingForm";
import { ChecklistMappingsTable } from "../ui/ChecklistMappingsTable";
import { validateChecklistId } from "../utils/checklist-id";
import {
  scheduleDayOfWeekLabels,
  scheduleFrequencyLabels,
  scheduleTypeLabels,
  statusLabels,
} from "../utils/labels";

export function ChecklistDetailPage() {
  const params = useParams({ strict: false });
  const idParam = params.id;
  const nav = useNavigate();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [addKpiModalOpen, setAddKpiModalOpen] = useState(false);
  const [editingMapping, setEditingMapping] = useState<{
    id: string;
    sopId: string | null;
    flowId: string | null;
    positionId: string | null;
    departmentId: string | null;
  } | null>(null);

  const checklistId = validateChecklistId(idParam);

  const checklist = useChecklistQuery(checklistId ?? "");
  const mappings = useChecklistMappings(checklistId ?? "");
  const kpiMappings = useChecklistKpiMappings(checklistId ?? "");
  const createMapping = useCreateChecklistMapping(checklistId ?? "");
  const updateMapping = useUpdateChecklistMapping(checklistId ?? "");
  const canEdit = useActionPermission(["checklists:update"]);
  const detailTour = useChecklistDetailTour();

  if (!checklistId) {
    return <ChecklistNotFoundState />;
  }

  if (checklist.isLoading) {
    return <ChecklistLoadingState />;
  }

  if (!checklist.data) {
    return <ChecklistErrorState />;
  }

  const checklistData = checklist.data;

  return (
    <>
      <Header />
      <Main>
        <div className="space-y-6">
          {/* Header Section */}
          <div
            className="flex items-center justify-between"
            data-tourid="detail-header"
          >
            <div>
              <div className="flex items-center space-x-2">
                <h1 className="font-bold text-2xl">{checklistData.title}</h1>

                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 rounded-full"
                  onClick={detailTour.startTour}
                >
                  <HelpCircle className="size-5" />
                </Button>
              </div>

              <p className="mt-1 text-muted-foreground">
                ລາຍລະອຽດ Checklist ແລະການຜູກກັບ
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  nav({ to: "/app/checklists" });
                }}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                ກັບ
              </Button>
              {canEdit && (
                <Button
                  onClick={() => {
                    nav({
                      to: "/app/checklists/$id/edit",
                      params: { id: checklistId },
                    });
                  }}
                >
                  <Edit className="mr-2 h-4 w-4" />
                  ແກ້ໄຂ
                </Button>
              )}
            </div>
          </div>

          {/* Checklist Details Section */}
          <div className="space-y-6 rounded-xl border bg-card p-6">
            <h2 className="font-semibold text-xl">ຂໍ້ມູນພື້ນຖານ</h2>

            <div
              className="grid grid-cols-2 gap-4"
              data-tourid="detail-basic-info"
            >
              <div>
                <p className="font-medium text-muted-foreground text-sm">
                  ຊື່ Checklist
                </p>
                <p className="font-medium text-base">{checklistData.title}</p>
              </div>
              <div>
                <p className="font-medium text-muted-foreground text-sm">
                  ປະເພດ
                </p>
                <p className="text-base">{checklistData.typeName || "-"}</p>
              </div>
              <div>
                <p className="font-medium text-muted-foreground text-sm">
                  ສະຖານະ
                </p>
                <Badge
                  variant={
                    checklistData.status === "active"
                      ? "default"
                      : checklistData.status === "draft"
                        ? "secondary"
                        : "outline"
                  }
                >
                  {statusLabels[checklistData.status] || checklistData.status}
                </Badge>
              </div>
              <div>
                <p className="font-medium text-muted-foreground text-sm">
                  ປະເພດການໃຊ້ງານ
                </p>
                <p className="text-base">
                  {scheduleTypeLabels[checklistData.scheduleType] ||
                    checklistData.scheduleType}
                  {checklistData.scheduleType === "scheduled" &&
                    checklistData.scheduleFrequency && (
                      <span className="text-muted-foreground">
                        {" "}
                        (
                        {scheduleFrequencyLabels[
                          checklistData.scheduleFrequency
                        ] ?? checklistData.scheduleFrequency}
                        {checklistData.scheduleFrequency === "weekly" &&
                          checklistData.scheduleDayOfWeek != null && (
                            <>
                              {" "}
                              {scheduleDayOfWeekLabels[
                                checklistData.scheduleDayOfWeek
                              ] ?? `ວັນທີ່ ${checklistData.scheduleDayOfWeek}`}
                            </>
                          )}
                        {checklistData.scheduleFrequency === "monthly" &&
                          checklistData.scheduleDayOfMonth != null && (
                            <> ວັນທີ່ {checklistData.scheduleDayOfMonth}</>
                          )}
                        )
                      </span>
                    )}
                </p>
              </div>
              {checklistData.departmentName && (
                <div>
                  <p className="font-medium text-muted-foreground text-sm">
                    ພະແນກ
                  </p>
                  <p className="text-base">{checklistData.departmentName}</p>
                </div>
              )}
              {checklistData.description && (
                <div className="col-span-2">
                  <p className="font-medium text-muted-foreground text-sm">
                    ຄຳອະທິບາຍ
                  </p>
                  <p className="text-base">{checklistData.description}</p>
                </div>
              )}
            </div>

            {/* Settings */}
            <div data-tourid="detail-settings">
              <h3 className="mb-3 font-semibold text-lg">ການຕັ້ງຄ່າ</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <Checkbox checked={checklistData.requireAllItems} disabled />
                  <span className="text-sm">ຕ້ອງຕິກຄຣອບທຸກຂໍ້ກ່ອນຈົບ</span>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox checked={checklistData.requirePhoto} disabled />
                  <span className="text-sm">ຕ້ອງຖ່າຍຮູບ</span>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={checklistData.requireNotesOnFail}
                    disabled
                  />
                  <span className="text-sm">ຕ້ອງໃສ່ໝາຍເຫດຖ້າບໍ່ຕິກ</span>
                </div>
              </div>
            </div>

            {/* Checklist Items */}
            {checklistData.items && checklistData.items.length > 0 && (
              <div data-tourid="detail-items">
                <h3 className="mb-3 font-semibold text-lg">
                  ລາຍການງານ ({checklistData.items.length} ລາຍການ)
                </h3>
                <div className="space-y-2">
                  {checklistData.items.map(
                    (
                      item: {
                        id: string;
                        title: string;
                        description: string | null;
                        order: number;
                        isRequired: boolean;
                        allowNotes: boolean;
                        formId?: string | null;
                        formName?: string | null;
                      },
                      index: number,
                    ) => (
                      <div
                        key={item.id}
                        className="flex items-start gap-3 rounded-lg border p-3"
                      >
                        <span className="min-w-[40px] font-medium text-muted-foreground text-sm">
                          #{index + 1}
                        </span>
                        <div className="flex-1">
                          <div className="mb-1 flex flex-wrap items-center gap-2">
                            <p className="font-medium">{item.title}</p>
                            {item.isRequired && (
                              <Badge variant="destructive" className="text-xs">
                                ຈຳເປັນ
                              </Badge>
                            )}
                            {item.allowNotes && (
                              <Badge variant="secondary" className="text-xs">
                                ອະນຸຍາດໝາຍເຫດ
                              </Badge>
                            )}
                            {item.formId && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-7 gap-1 text-xs"
                                onClick={() =>
                                  nav({
                                    to: "/app/forms/$id",
                                    params: { id: item.formId! },
                                  })
                                }
                              >
                                <ExternalLink className="size-3.5" />
                                {item.formName || "ເປີດຟອມ"}
                              </Button>
                            )}
                          </div>
                          {item.description && (
                            <p className="text-muted-foreground text-sm">
                              {item.description}
                            </p>
                          )}
                        </div>
                      </div>
                    ),
                  )}
                </div>
              </div>
            )}

            {/* Metadata */}
            <div>
              <h3 className="mb-3 font-semibold text-lg">ຂໍ້ມູນເພີ່ມເຕີມ</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="font-medium text-muted-foreground">ວັນທີ່ສ້າງ</p>
                  <p>{formatDateTime(checklistData.createdAt)}</p>
                </div>
                <div>
                  <p className="font-medium text-muted-foreground">
                    ວັນທີ່ແກ້ໄຂລ່າສຸດ
                  </p>
                  <p>{formatDateTime(checklistData.updatedAt)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Checklist Mappings Section */}
          <div className="space-y-4" data-tourid="detail-mappings">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-xl">ການຜູກກັບ</h2>
                <p className="mt-1 text-muted-foreground">
                  ຈັດການການຜູກກັບ Checklist ກັບ SOP, Flow, ຕໍາແໜ່ງ, ແລະພະແນກ
                </p>
              </div>
              <Button onClick={() => setCreateModalOpen(true)}>
                ເພີ່ມການຜູກກັບ
              </Button>
            </div>

            <ChecklistMappingsTable
              data={mappings.data?.data ?? []}
              isLoading={mappings.isLoading}
              onEdit={(mapping) => {
                setEditingMapping({
                  id: mapping.id,
                  sopId: mapping.sopId,
                  flowId: mapping.flowId,
                  positionId: mapping.positionId,
                  departmentId: mapping.departmentId,
                });
                setEditModalOpen(true);
              }}
              onDelete={async (mappingId) => {
                await checklistsApi.deleteMapping(mappingId);
                mappings.refetch();
              }}
            />

            <Modal
              open={createModalOpen}
              onOpenChange={setCreateModalOpen}
              title="ເພີ່ມການຜູກກັບ"
              size="md"
            >
              <div className="max-h-[calc(100vh-12rem)] overflow-y-auto">
                <ChecklistMappingForm
                  checklistDepartmentId={
                    checklistData.departmentId ?? undefined
                  }
                  onSubmit={async (values) => {
                    if (!checklistId) return;
                    await createMapping.mutateAsync(values);
                    setCreateModalOpen(false);
                    mappings.refetch();
                  }}
                  submitting={createMapping.isPending}
                />
              </div>
            </Modal>

            <Modal
              open={editModalOpen}
              onOpenChange={(open) => {
                setEditModalOpen(open);
                if (!open) {
                  setEditingMapping(null);
                }
              }}
              title="ແກ້ໄຂການຜູກກັບ"
              size="md"
            >
              <div className="max-h-[calc(100vh-12rem)] overflow-y-auto">
                <ChecklistMappingForm
                  checklistDepartmentId={
                    checklistData.departmentId ?? undefined
                  }
                  initialValues={
                    editingMapping
                      ? {
                          sopId: editingMapping.sopId,
                          flowId: editingMapping.flowId,
                          positionId: editingMapping.positionId,
                          departmentId: editingMapping.departmentId,
                        }
                      : undefined
                  }
                  onSubmit={async (values) => {
                    if (!editingMapping) return;
                    await updateMapping.mutateAsync({
                      mappingId: editingMapping.id,
                      data: values,
                    });
                    setEditModalOpen(false);
                    setEditingMapping(null);
                    mappings.refetch();
                  }}
                  submitting={updateMapping.isPending}
                />
              </div>
            </Modal>
          </div>

          {/* KPI ທີ່ດຶງຄ່າຈາກ checklist ນີ້ (Phase 4: Checklist → KPI) */}
          <div className="space-y-4" data-tourid="detail-kpi-mappings">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-xl">
                  KPI ທີ່ດຶງຄ່າຈາກ checklist ນີ້
                </h2>
                <p className="mt-1 text-muted-foreground text-sm">
                  ເມື່ອມີການເຮັດ checklist ແລ້ວ (ສຳເລັດ) ຄ່າ KPI ທີ່ຜູກຢູ່ຈະຖືກອັບເດດອັດຕະໂນມັດ
                </p>
              </div>
              {canEdit && (
                <Button onClick={() => setAddKpiModalOpen(true)}>
                  ເພີ່ມ KPI
                </Button>
              )}
            </div>
            <ChecklistKpiMappingsTable
              checklistId={checklistId}
              data={kpiMappings.data?.data ?? []}
              isLoading={kpiMappings.isLoading}
              onDeleted={() => kpiMappings.refetch()}
            />
            <ChecklistKpiMappingAddModal
              checklistId={checklistId}
              open={addKpiModalOpen}
              onOpenChange={setAddKpiModalOpen}
              excludedKpiIds={(kpiMappings.data?.data ?? []).map(
                (m) => m.kpiId,
              )}
              onSuccess={() => kpiMappings.refetch()}
            />
          </div>
        </div>

        <ChecklistDetailTour
          run={detailTour.run}
          onCallback={detailTour.handleJoyrideCallback}
        />
      </Main>
    </>
  );
}
